import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from '../../components/Pagination';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface Analytics {
  total_customers: number;
  active_licenses: number;
  expired_licenses: number;
  revoked_licenses: number;
  expiring_90_days: number;
  expiring_60_days: number;
  expiring_30_days: number;
}

interface License {
  customer_id: string;
  company_name: string;
  contact_email: string;
  license_status: string | null;
  license_expires: string | null;
  max_nodes: number | null;
  ha_enabled: boolean | null;
}

const getDaysUntilExpiry = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffTime = expiry.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export default function LicensesPage() {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'expired' | '30' | '60' | '90'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'revoked'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      const [analyticsRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/analytics`),
        axios.get(`${API_URL}/admin/customers`),
      ]);
      setAnalytics(analyticsRes.data);
      setLicenses(customersRes.data.filter((c: License) => c.license_status));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredLicenses = () => {
    let filtered = licenses;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(license =>
        license.company_name.toLowerCase().includes(term) ||
        license.customer_id.toLowerCase().includes(term) ||
        license.contact_email.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(license => license.license_status === statusFilter);
    }

    // Expiry filter
    if (filter !== 'all') {
      filtered = filtered.filter(license => {
        const days = getDaysUntilExpiry(license.license_expires);
        if (days === null) return false;

        switch (filter) {
          case 'expired':
            return days < 0;
          case '30':
            return days >= 0 && days <= 30;
          case '60':
            return days > 30 && days <= 60;
          case '90':
            return days > 60 && days <= 90;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const filteredLicenses = getFilteredLicenses();
  const paginatedLicenses = filteredLicenses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getExpiryBadge = (daysLeft: number | null) => {
    if (daysLeft === null) return null;
    if (daysLeft < 0) return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Expired</span>;
    if (daysLeft <= 30) return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">{daysLeft}d left</span>;
    if (daysLeft <= 60) return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">{daysLeft}d left</span>;
    if (daysLeft <= 90) return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">{daysLeft}d left</span>;
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{daysLeft}d left</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">License Overview</h1>
        <p className="text-gray-600">Monitor license status and upcoming renewals</p>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <button
            onClick={() => setFilter('expired')}
            className={`bg-white rounded-lg shadow p-4 border-l-4 text-left transition-all hover:shadow-md ${
              filter === 'expired' ? 'ring-2 ring-red-500' : ''
            } ${analytics.expired_licenses > 0 ? 'border-red-500' : 'border-gray-300'}`}
          >
            <div className="text-sm text-gray-500">Expired</div>
            <div className={`text-3xl font-bold ${analytics.expired_licenses > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {analytics.expired_licenses}
            </div>
            {analytics.expired_licenses > 0 && (
              <div className="text-xs text-red-500 mt-1">Requires attention</div>
            )}
          </button>

          <button
            onClick={() => setFilter('30')}
            className={`bg-white rounded-lg shadow p-4 border-l-4 text-left transition-all hover:shadow-md ${
              filter === '30' ? 'ring-2 ring-red-400' : ''
            } ${analytics.expiring_30_days > 0 ? 'border-red-400' : 'border-gray-300'}`}
          >
            <div className="text-sm text-gray-500">30 Days</div>
            <div className={`text-3xl font-bold ${analytics.expiring_30_days > 0 ? 'text-red-500' : 'text-gray-400'}`}>
              {analytics.expiring_30_days}
            </div>
            {analytics.expiring_30_days > 0 && (
              <div className="text-xs text-red-400 mt-1">Urgent</div>
            )}
          </button>

          <button
            onClick={() => setFilter('60')}
            className={`bg-white rounded-lg shadow p-4 border-l-4 text-left transition-all hover:shadow-md ${
              filter === '60' ? 'ring-2 ring-orange-400' : ''
            } ${analytics.expiring_60_days > analytics.expiring_30_days ? 'border-orange-400' : 'border-gray-300'}`}
          >
            <div className="text-sm text-gray-500">60 Days</div>
            <div className={`text-3xl font-bold ${analytics.expiring_60_days > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
              {analytics.expiring_60_days}
            </div>
          </button>

          <button
            onClick={() => setFilter('90')}
            className={`bg-white rounded-lg shadow p-4 border-l-4 text-left transition-all hover:shadow-md ${
              filter === '90' ? 'ring-2 ring-yellow-400' : ''
            } ${analytics.expiring_90_days > analytics.expiring_60_days ? 'border-yellow-400' : 'border-gray-300'}`}
          >
            <div className="text-sm text-gray-500">90 Days</div>
            <div className={`text-3xl font-bold ${analytics.expiring_90_days > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
              {analytics.expiring_90_days}
            </div>
          </button>

          <button
            onClick={() => setFilter('all')}
            className={`bg-white rounded-lg shadow p-4 border-l-4 border-green-500 text-left transition-all hover:shadow-md ${
              filter === 'all' ? 'ring-2 ring-green-500' : ''
            }`}
          >
            <div className="text-sm text-gray-500">Active</div>
            <div className="text-3xl font-bold text-green-600">{analytics.active_licenses}</div>
            <div className="text-xs text-gray-400 mt-1">{analytics.total_customers} customers</div>
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search by company, customer ID, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'revoked')}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
        {filter !== 'all' && (
          <button
            onClick={() => setFilter('all')}
            className="text-sm text-blue-600 hover:underline"
          >
            Clear expiry filter
          </button>
        )}
      </div>

      {/* Filter Label */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-700">
          {filter === 'all' && 'All Licenses'}
          {filter === 'expired' && 'Expired Licenses'}
          {filter === '30' && 'Expiring in 30 Days'}
          {filter === '60' && 'Expiring in 31-60 Days'}
          {filter === '90' && 'Expiring in 61-90 Days'}
          <span className="text-gray-400 font-normal ml-2">({filteredLicenses.length})</span>
        </h2>
      </div>

      {/* Licenses Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max Nodes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HA</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginatedLicenses.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No licenses found matching your criteria.
                </td>
              </tr>
            ) : (
              paginatedLicenses.map((license) => {
                const daysLeft = getDaysUntilExpiry(license.license_expires);
                return (
                  <tr key={license.customer_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{license.company_name}</div>
                      <div className="text-sm text-gray-500">{license.customer_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${
                        license.license_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {license.license_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">
                          {license.license_expires ? new Date(license.license_expires).toLocaleDateString() : '-'}
                        </span>
                        {getExpiryBadge(daysLeft)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {license.max_nodes ?? '-'}
                    </td>
                    <td className="px-6 py-4">
                      {license.ha_enabled !== null ? (
                        license.ha_enabled ? (
                          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Yes</span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">No</span>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {license.contact_email}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalItems={filteredLicenses.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
    </div>
  );
}
